import type { AuthProvider } from "@refinedev/core";
import { sessionService } from "../services/sessionService";
import { authService } from "../services/authService";
import type {
  MeDetailsQuery,
  LoginMutation,
  LogoutMutation,
  StartLinkRecoveryMutation,
  ResetPasswordMutation,
} from "../graphql/gmbh/schema/gmbh-types";
import {
  MeDetailsDocument,
  LoginDocument,
  LogoutDocument,
  StartLinkRecoveryDocument,
  ResetPasswordDocument,
} from "../graphql/gmbh/schema/gmbh-types";
import { requestGmbh } from "./utils/graphqlHelper";

export const authProvider: AuthProvider = {
  login: async ({ email, password }) => {
    try {
      const response = await requestGmbh<LoginMutation>(LoginDocument, {
        input: { email, password, nativeApp: false },
      });

      const session = response.data.loginWithPassword;

      if (session?.expiresAt) {
        sessionService.startSessionExtension(session.expiresAt);
        await authService.fetchAccessToken();

        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Bitte überprüfe deine E-Mail-Adresse oder dein Passwort.",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "LoginError",
          message: "Bitte überprüfe deine E-Mail-Adresse oder dein Passwort.",
        },
      };
    }
  },

  logout: async () => {
    try {
      await requestGmbh<LogoutMutation>(LogoutDocument, { input: { nativeApp: false } });

      sessionService.stopSessionExtension();
      authService.clearAuth();

      return {
        success: true,
        redirectTo: "/login",
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "LogoutError",
          message: error.message || "An error occurred during logout",
        },
      };
    }
  },

  check: async () => {
    const isAuthenticated = await authService.checkAuthentication();

    if (isAuthenticated) {
      return { authenticated: true };
    }

    return {
      authenticated: false,
      redirectTo: "/login",
    };
  },

  getIdentity: async () => {
    try {
      const response = await requestGmbh<MeDetailsQuery>(MeDetailsDocument);

      return response.data.me;
    } catch (error) {
      return null;
    }
  },

  onError: async (error) => {
    console.error(error);
    return { error };
  },

  forgotPassword: async ({ email }) => {
    try {
      const response = await requestGmbh<StartLinkRecoveryMutation>(StartLinkRecoveryDocument, {
        input: { email, nativeApp: false },
      });

      if (response.data.startLinkRecovery.emailIsSent) {
        return {
          success: true,
          redirectTo: "/login",
        };
      }

      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: "Failed to send recovery email",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "ForgotPasswordError",
          message: error.message || "An error occurred while requesting password reset",
        },
      };
    }
  },

  updatePassword: async ({ password }) => {
    try {
      const response = await requestGmbh<ResetPasswordMutation>(ResetPasswordDocument, {
        input: { newPassword: password },
      });

      const session = response.data.resetPassword;

      if (session?.expiresAt) {
        sessionService.startSessionExtension(session.expiresAt);
        return {
          success: true,
          redirectTo: "/",
        };
      }

      return {
        success: false,
        error: {
          name: "UpdatePasswordError",
          message: "Failed to update password",
        },
      };
    } catch (error: any) {
      return {
        success: false,
        error: {
          name: "UpdatePasswordError",
          message: error.message || "An error occurred while updating password",
        },
      };
    }
  },
};
